import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Plus, Trash2, Save } from 'lucide-react';

const ZoneSelector = ({ 
  cameraId, 
  zoneType = 'parking', // 'parking', 'intrusion', 'restricted', 'safe'
  onSave,
  title = 'Zone Setup',
  description = 'Click on the live camera preview to draw zones. Right-click to remove the last point.'
}) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zones, setZones] = useState([]);

  // Zone Intelligence presets (loitering/intrusion, entry/exit, in-store, vehicle)
  const ZONE_INTELLIGENCE_RULES = {
    'loitering-intrusion': { rule_type: 'dwell', classes: ['person'], defaultThreshold: 30 },
    'entry-exit-counting': { rule_type: 'line_cross', classes: ['person'], defaultThreshold: null },
    'instore-foot-traffic': { rule_type: 'count', classes: ['person'], defaultThreshold: 10 },
    'vehicle-suspicious-zone': { rule_type: 'count', classes: ['person', 'vehicle'], defaultThreshold: 3 },
  };
  const zoneIntelligenceRule = ZONE_INTELLIGENCE_RULES[zoneType];
  const isLineMode = zoneType === 'entry-exit-counting';
  const [threshold, setThreshold] = useState(zoneIntelligenceRule?.defaultThreshold || 5);

  const handleCanvasClick = (event) => {
    if (!canvasRef.current || !imageLoaded) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    
    const clampedX = Math.max(0, Math.min(1, x));
    const clampedY = Math.max(0, Math.min(1, y));

    setPoints(prev => [...prev, [clampedX, clampedY]]);
  };

  const handleRightClick = (event) => {
    event.preventDefault();
    if (points.length > 0) {
      setPoints(prev => prev.slice(0, -1));
    }
  };

  const drawPolygon = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw saved zones
    zones.forEach((zone, index) => {
      ctx.strokeStyle = '#52c41a';
      ctx.lineWidth = 3;
      ctx.fillStyle = 'rgba(82, 196, 26, 0.2)';

      ctx.beginPath();
      zone.points.forEach((point, pointIndex) => {
        const x = point[0] * canvas.width;
        const y = point[1] * canvas.height;
        
        if (pointIndex === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      if (!isLineMode && zone.points.length >= 3) {
        ctx.closePath();
        ctx.fill();
      }
      ctx.stroke();

      // Draw zone label
      if (zone.points.length > 0) {
        const centerX = zone.points.reduce((sum, [x]) => sum + x, 0) / zone.points.length * canvas.width;
        const centerY = zone.points.reduce((sum, [, y]) => sum + y, 0) / zone.points.length * canvas.height;
        ctx.fillStyle = '#000';
        ctx.font = '12px Arial';
        ctx.fillText(`Zone ${index + 1}`, centerX - 20, centerY);
      }
    });

    // Draw current points
    if (points.length === 0) return;

    ctx.strokeStyle = '#1890ff';
    ctx.lineWidth = 3;
    ctx.fillStyle = 'rgba(24, 144, 255, 0.2)';

    ctx.beginPath();
    points.forEach((point, index) => {
      const x = point[0] * canvas.width;
      const y = point[1] * canvas.height;
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
      
      // Draw point
      ctx.fillStyle = '#1890ff';
      ctx.fillRect(x - 4, y - 4, 8, 8);
      ctx.fillStyle = 'rgba(24, 144, 255, 0.2)';
    });

    if (!isLineMode && points.length >= 3) {
      ctx.closePath();
      ctx.fill();
    }
    ctx.stroke();

    // Draw instructions
    ctx.fillStyle = '#ffffff';
    ctx.font = '14px Arial';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    const text = `Points: ${points.length} | Left click: Add | Right click: Remove`;
    ctx.strokeText(text, 10, 25);
    ctx.fillText(text, 10, 25);
  };

  const addZone = () => {
    if (isLineMode) {
      if (points.length !== 2) {
        alert('Please click exactly 2 points to draw a line');
        return;
      }
    } else if (points.length < 3) {
      alert('Please select at least 3 points to create a zone');
      return;
    }

    const newZone = {
      zone_id: `zone_${Date.now()}`,
      name: `${zoneType.charAt(0).toUpperCase() + zoneType.slice(1)} Zone ${zones.length + 1}`,
      zone_type: zoneType,
      points: [...points]
    };

    setZones([...zones, newZone]);
    setPoints([]);
  };

  const deleteZone = (index) => {
    const newZones = zones.filter((_, i) => i !== index);
    setZones(newZones);
  };

  const saveZones = async () => {
    if (zones.length === 0) {
      alert('Please create at least one zone');
      return;
    }

    console.log('💾 Saving zones:', zones);

    try {
      let endpoint, requestBody;
      
      if (zoneType === 'parking') {
        // Parking zones: POST /api/parking/zones/{camera_id} with array of zones
        endpoint = `/api/parking/zones/${cameraId}`;
        requestBody = zones.map(zone => ({
          ...zone,
          coordinates: zone.points
        }));
      } else if (zoneType === 'restricted') {
        // Restricted area PH: POST /api/restricted-area-ph/set-zone/{camera_id} with single polygon
        endpoint = `/api/restricted-area-ph/set-zone/${cameraId}`;
        if (zones.length > 0) {
          requestBody = {
            polygon_points: zones[0].points // Use first zone's points
          };
        }
      } else if (zoneType === 'intrusion') {
        // Zone intrusion: POST /api/detection/zone-intrusion/set-zone/{camera_id} with single polygon
        endpoint = `/api/detection/zone-intrusion/set-zone/${cameraId}`;
        if (zones.length > 0) {
          requestBody = {
            polygon_points: zones[0].points // Use first zone's points
          };
        }
      } else if (zoneIntelligenceRule) {
        // Zone Intelligence presets: POST /api/cameras/{camera_id}/zone-config
        endpoint = `/api/cameras/${cameraId}/zone-config`;
        requestBody = {
          preset: zoneType,
          rule_type: zoneIntelligenceRule.rule_type,
          classes: zoneIntelligenceRule.classes,
          threshold,
          ...(isLineMode
            ? { line_norm: zones[0].points }
            : { polygon_norm: zones[0].points })
        };
      } else {
        // Generic endpoint for other zone types
        endpoint = `/api/${zoneType}/zones/${cameraId}`;
        requestBody = zones.map(zone => ({
          ...zone,
          coordinates: zone.points
        }));
      }
        
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('csense_auth_token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        console.log(`${zoneType} zones saved successfully`);
        onSave?.(zones);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(`Failed to save ${zoneType} zones:`, errorData);
        alert(`Failed to save zones: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Error saving ${zoneType} zones:`, error);
      alert(`Error saving zones: ${error.message}`);
    }
  };

  const loadExistingZones = async () => {
    if (zoneIntelligenceRule) {
      try {
        const response = await fetch(`/api/cameras/${cameraId}/zone-config?preset=${zoneType}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('csense_auth_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          const config = data.config;
          if (config) {
            setThreshold(config.threshold || zoneIntelligenceRule.defaultThreshold || 5);
            const loadedPoints = isLineMode ? config.line_norm : config.polygon_norm;
            if (loadedPoints && loadedPoints.length > 0) {
              setZones([{
                zone_id: `zone_${Date.now()}`,
                name: `${zoneType} Zone 1`,
                zone_type: zoneType,
                points: loadedPoints
              }]);
            }
          }
        }
      } catch (error) {
        console.error(`Error loading ${zoneType} config:`, error);
      }
      return;
    }

    try {
      let zoneTypeParam;

      if (zoneType === 'parking') {
        zoneTypeParam = 'parking';
      } else if (zoneType === 'restricted') {
        zoneTypeParam = 'restricted_area';
      } else if (zoneType === 'intrusion') {
        zoneTypeParam = 'intrusion';
      } else if (zoneType === 'safe') {
        zoneTypeParam = 'safe_zone';
      } else {
        return;
      }
      
      const response = await fetch(`/api/zones/${cameraId}?zone_type=${zoneTypeParam}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('csense_auth_token')}`
        }
      });

      if (response && response.ok) {
        const data = await response.json();
        
        if (zoneType === 'parking') {
          if (data.zones && data.zones.length > 0) {
            const loadedZones = data.zones.map((zone, index) => ({
              zone_id: zone.zone_id || `zone_${Date.now()}_${index}`,
              name: zone.name || `Parking Zone ${index + 1}`,
              zone_type: 'parking',
              points: zone.coordinates || zone.points || []
            }));
            setZones(loadedZones);
          }
        } else if (zoneType === 'restricted' || zoneType === 'intrusion' || zoneType === 'safe') {
          if (data.polygon_points && data.polygon_points.length > 0) {
            const loadedZone = {
              zone_id: `zone_${Date.now()}`,
              name: `${zoneType.charAt(0).toUpperCase() + zoneType.slice(1)} Zone 1`,
              zone_type: zoneType,
              points: data.polygon_points
            };
            setZones([loadedZone]);
          }
        }
      }
    } catch (error) {
      console.error('Error loading existing zones:', error);
    }
  };

  useEffect(() => {
    if (imgRef.current) {
      const token = localStorage.getItem('csense_auth_token');
      imgRef.current.src = `/api/cameras/${cameraId}/live-stream?token=${token}`;
    }
    
    // Load existing zones when component mounts
    if (cameraId) {
      loadExistingZones();
    }
  }, [cameraId, zoneType]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    if (canvasRef.current && imgRef.current) {
      const canvas = canvasRef.current;
      const img = imgRef.current;
      
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      
      console.log('📺 Video stream dimensions:', {
        width: canvas.width,
        height: canvas.height
      });
      
      drawPolygon();
    }
  };

  useEffect(() => {
    drawPolygon();
  }, [points, zones, imageLoaded]);

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      </div>

      {/* Live Camera Preview with Drawing Canvas */}
      <div className="relative mb-4 bg-black rounded-lg overflow-hidden">
        <img
          ref={imgRef}
          className="w-full h-auto"
          onLoad={handleImageLoad}
          onError={() => setImageLoaded(false)}
          style={{ 
            display: imageLoaded ? 'block' : 'none',
            width: '100%',
            height: 'auto'
          }}
          alt="Live camera feed"
        />
        
        {!imageLoaded && (
          <div className="w-full h-96 bg-gray-800 flex items-center justify-center text-white">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
              <p>Loading live camera feed...</p>
            </div>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 cursor-crosshair"
          onClick={handleCanvasClick}
          onContextMenu={handleRightClick}
          style={{ 
            display: imageLoaded ? 'block' : 'none',
            width: '100%',
            height: 'auto'
          }}
        />
      </div>

      {/* Controls */}
      <div className="mb-4">
        <div className="flex gap-2 mb-4">
          <Button
            onClick={addZone}
            disabled={isLineMode ? (points.length !== 2 || !imageLoaded) : (points.length < 3 || !imageLoaded)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {isLineMode ? `Add Line (${points.length}/2 points)` : `Add Zone (${points.length} points)`}
          </Button>
          
          <Button 
            onClick={() => setPoints([])}
            disabled={points.length === 0}
            variant="outline"
          >
            Clear Points
          </Button>
          
          {!isLineMode && (
            <Button
              onClick={() => {
                if (!canvasRef.current) return;

                // Create full-frame zone with normalized coordinates (0-1 range)
                const fullFramePoints = [[0.0, 0.0], [1.0, 0.0], [1.0, 1.0], [0.0, 1.0]];
                setPoints(fullFramePoints);
                console.log('🎯 Full frame zone created with normalized coordinates:', fullFramePoints);
              }}
              disabled={!imageLoaded}
              variant="outline"
              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
            >
              Full Frame Zone
            </Button>
          )}

          {zoneIntelligenceRule && zoneIntelligenceRule.rule_type !== 'line_cross' && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">
                {zoneIntelligenceRule.rule_type === 'dwell' ? 'Dwell threshold (sec)' : 'Count threshold'}
              </label>
              <input
                type="number"
                min="1"
                value={threshold}
                onChange={(e) => setThreshold(parseInt(e.target.value, 10) || 1)}
                className="w-20 border rounded px-2 py-1 text-sm"
              />
            </div>
          )}

          <Button 
            onClick={saveZones}
            disabled={zones.length === 0}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            Save All Zones
          </Button>
        </div>
      </div>

      {/* Zone List */}
      <div className="mt-4">
        <h4 className="font-medium mb-2">Configured Zones ({zones.length}):</h4>
        <div className="space-y-2">
          {zones.map((zone, index) => (
            <div key={zone.zone_id} className="flex items-center justify-between p-2 border rounded">
              <Badge variant="default" className="bg-green-100 text-green-800">
                {zone.name} ({zone.points.length} points)
              </Badge>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => deleteZone(index)}
                className="flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </Button>
            </div>
          ))}
        </div>
        {zones.length === 0 && (
          <p className="text-sm text-gray-500">
            No zones configured. Draw points on the camera feed and click "Add Zone".
          </p>
        )}
      </div>
    </div>
  );
};

export default ZoneSelector;